<template>
  <div class="page">
    <header class="page-header">
      <div class="page-header-bar">
        <button type="button" class="back-button" @click="goBack">取消</button>
        <span>扫描设备码</span>
      </div>
      <div class="page-header-content">
        <h1>全屏扫码</h1>
        <p>请将设备条形码横向放入取景区域，长条码识别失败可优先用相册识别</p>
      </div>
    </header>

    <main class="page-body">
      <Html5BarcodeScanner
        ref="scannerRef"
        @success="handleSuccess"
        @detected="handleDetected"
        @error="handleError"
      />

      <section class="status-panel">
        <div class="status-text" :class="{ success: hasScanResult }">{{ statusText }}</div>

        <div class="device-result">
          <span class="device-label">设备编码：</span>
          <span class="device-code">{{ previewCode || '等待扫码' }}</span>
        </div>

        <div class="scan-actions">
          <van-button plain size="small" type="primary" @click="handleRescan">重新扫码</van-button>
          <!-- <van-button plain size="small" type="primary" :loading="fileScanning" @click="handlePickImage">从相册识别</van-button> -->
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { showToast } from 'vant'
import Html5BarcodeScanner from '@/components/install/Html5BarcodeScanner.vue'
import { useMainStore } from '@/store'
import { normalizeTerminalCode } from './terminal-code'

type ScannerResult = {
  decodedText: string
  formatName: string
}

const router = useRouter()
const store = useMainStore()
const scannerRef = ref<InstanceType<typeof Html5BarcodeScanner> | null>(null)
const statusText = ref('请将设备条形码横向放入取景区域')
const previewCode = ref('')
const hasScanResult = ref(false)

if (!store.installContext) {
  router.replace('/install/area')
} else if (!store.installSelection.terminalId) {
  router.replace('/install/location')
}

const goBack = () => {
  router.replace('/install/device')
}

const applyDetectedCode = (value: string) => {
  const terminalCode = normalizeTerminalCode(value)
  previewCode.value = terminalCode
  hasScanResult.value = true

  if (!terminalCode) {
    statusText.value = '设备码无效，请重新扫码'
    showToast('设备码无效，请重新扫码')
    return false
  }

  store.setInstallSelection({ terminalCode })
  statusText.value = '设备码识别成功，正在返回'
  showToast({ type: 'success', message: '设备码识别成功' })
  window.setTimeout(() => {
    router.replace('/install/device')
  }, 200)
  return true
}

const handleSuccess = (value: string) => {
  applyDetectedCode(value)
}

const handleDetected = (_result: ScannerResult) => {}

const handleError = () => {
  if (!hasScanResult.value) {
    statusText.value = '设备码识别失败，请调整距离后重试'
  }
}

const handleRescan = async () => {
  previewCode.value = ''
  hasScanResult.value = false
  statusText.value = '请将设备条形码横向放入取景区域'
  await scannerRef.value?.restart()
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  overflow: hidden;
  background: #020617;
  color: #fff;
}

.page-header {
  flex: none;
  padding-top: env(safe-area-inset-top);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.88) 60%, rgba(15, 23, 42, 0.6) 100%);
}

.page-header-bar {
  display: grid;
  grid-template-columns: 64px 1fr 64px;
  align-items: center;
  height: 44px;
  padding: 0 12px;
  box-sizing: border-box;
  font-size: 17px;
  font-weight: 600;
}

.back-button {
  justify-self: start;
  border: 0;
  padding: 0;
  background: transparent;
  color: #93c5fd;
  font-size: 16px;
}

.page-header-content {
  padding: 8px 16px 10px;
  text-align: center;
}

.page-header h1 {
  margin: 0;
  font-size: 20px;
  line-height: 1.4;
}

.page-header p {
  max-width: 320px;
  margin: 4px auto 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(226, 232, 240, 0.88);
}

.page-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  gap: 10px;
  min-height: 0;
  padding: 6px 10px calc(env(safe-area-inset-bottom) + 10px);
  box-sizing: border-box;
}

.page-body :deep(.scanner) {
  flex: 1;
  min-height: 0;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 20px;
  overflow: hidden;
}

.status-panel {
  display: grid;
  gap: 8px;
  padding: 12px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.78) 100%);
  box-shadow: 0 14px 32px rgba(2, 6, 23, 0.28);
  backdrop-filter: blur(16px);
}

.status-text {
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
  color: rgba(226, 232, 240, 0.9);
}

.status-text.success {
  color: #86efac;
}

.device-result {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(30, 41, 59, 0.92);
  box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.18);
}

.device-label {
  flex: none;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.device-code {
  min-width: 0;
  flex: 1;
  text-align: right;
  word-break: break-all;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.scan-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.scan-actions :deep(.van-button) {
  flex: 1;
  border-color: rgba(96, 165, 250, 0.5);
  border-radius: 999px;
  color: #dbeafe;
  background: rgba(30, 41, 59, 0.42);
}
</style>
